import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { getTranslateContent } from '../utils';
import { eventBus } from 'services/eventBus';
import { Divider, Avatar, Tooltip } from 'antd';
import { SupplementKnowledgeAnswer } from 'services/api/supplementKnowledge';
import { ImageAnswer, VideoAnswer } from '../AnswerCell';
import { useTranslate } from '@tolgee/react';
import { useMemo } from 'react';

interface Props {
  answers: SupplementKnowledgeAnswer[];
  userName?: string;
  locate?: boolean;
}

export const Answer = (props: Props) => {
  const { answers, userName, locate = true } = props;
  const language = useSystemLanguageValue();

  const { nonTextAnswers, textAnswer } = useMemo(() => {
    return answers.reduce(
      (acc, answer) => {
        if (answer.msgType === 'TEXT') {
          const prevMessage = acc.textAnswer.message;
          acc.textAnswer.message = prevMessage
            ? prevMessage + '\n' + answer.message
            : answer.message;
          acc.textAnswer.messageId = answer.messageId || '';
          const translate = getTranslateContent(language, answer.translateMap);
          const prevTranslate = acc.textAnswer.translateContent;
          acc.textAnswer.translateContent = prevTranslate
            ? prevTranslate + '\n' + translate
            : translate;
        } else {
          acc.nonTextAnswers.push(answer);
        }
        return acc;
      },
      {
        nonTextAnswers: [],
        textAnswer: {
          message: undefined,
          messageId: undefined,
          translateContent: undefined,
        },
      } as {
        nonTextAnswers: SupplementKnowledgeAnswer[];
        textAnswer: {
          message?: string;
          messageId?: string;
          translateContent?: string;
        };
      },
    );
  }, [answers, language]);
  const { t } = useTranslate();

  const handleAnswerLocate = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!locate || !textAnswer?.messageId) return;
    e.stopPropagation();
    eventBus.emit('SCROLL_TO_MSG_BY_ID', textAnswer.messageId);
  };

  const content = (
    <div
      style={locate ? { cursor: 'pointer' } : undefined}
      className="px-[10px] py-2 rounded-sm flex-1 border border-solid h-[max-content] bg-[#F3F5F7] border-[#F3F5F7]"
      onClick={handleAnswerLocate}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-xs text-[#515359] whitespace-pre-wrap">{textAnswer?.message}</div>
      </div>
      <Divider className="!my-2" />
      <div className="flex flex-col gap-1">
        <div className="text-xs text-[#84868C] whitespace-pre-wrap">
          {textAnswer?.translateContent}
        </div>
        <div className="flex items-center gap-2">
          {nonTextAnswers?.map((answer) => {
            if (answer.msgType === 'IMAGE') {
              return (
                <ImageAnswer
                  answer={answer}
                  ellipsis={false}
                  textClassName="!text-xs !text-[#84868C]"
                />
              );
            }
            if (answer.msgType === 'VIDEO') {
              return (
                <VideoAnswer
                  answer={answer}
                  ellipsis={false}
                  textClassName="!text-xs !text-[#84868C]"
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex gap-2 max-h-[390px] overflow-y-auto">
      <Avatar className="!bg-[#D8E7FF]" shape="square" size={32}>
        {userName?.slice(0, 1).toUpperCase()}
      </Avatar>
      {locate ? (
        <Tooltip title={t('定位至答案')} placement="bottom">
          {content}
        </Tooltip>
      ) : (
        content
      )}
    </div>
  );
};
